import type React from "react";
import { useState, useRef, useEffect } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { categories } from "../constants/helpCentre";

const Collapsible: React.FC<{
  isOpen: boolean;
  id?: string;
  children: React.ReactNode;
}> = ({ isOpen, id, children }) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (innerRef.current) {
      setHeight(isOpen ? innerRef.current.scrollHeight : 0);
    }
  }, [isOpen, children]);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (!el) return;
      setHeight(isOpen ? el.scrollHeight : 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  return (
    <div
      id={id}
      style={{ maxHeight: height, willChange: "max-height" }}
      className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
      aria-hidden={!isOpen}
    >
      <div
        ref={innerRef}
        className={`transition-opacity duration-300 ease-out ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

const FormattedAnswer: React.FC<{ answer: string }> = ({ answer }) => {
  const formatAnswer = (text: string) => {
    const sentences = text.split(/\.\s+(?=[A-Z])/);
    const points: { main: string; sub: string[] }[] = [];

    sentences.forEach((sentence) => {
      const trimmed = sentence.trim();
      if (!trimmed) return;

      const hasColon = trimmed.includes(":");
      const startsWithAction =
        /^(To |Navigate |Click |Press |Fill |Choose |Enter |Use |Type |Scroll |By |Once |All |There |When |Note|Please)/i.test(
          trimmed,
        );

      if (hasColon && startsWithAction) {
        const [main, ...rest] = trimmed.split(":");
        points.push({
          main: main.trim() + (rest.length ? ":" : ""),
          sub: rest.length ? [rest.join(":").trim()] : [],
        });
      } else if (startsWithAction && points.length > 0) {
        points[points.length - 1].sub.push(
          trimmed + (trimmed.endsWith(".") ? "" : "."),
        );
      } else if (startsWithAction) {
        points.push({
          main: trimmed + (trimmed.endsWith(".") ? "" : "."),
          sub: [],
        });
      } else if (points.length > 0) {
        points[points.length - 1].sub.push(
          trimmed + (trimmed.endsWith(".") ? "" : "."),
        );
      } else {
        points.push({
          main: trimmed + (trimmed.endsWith(".") ? "" : "."),
          sub: [],
        });
      }
    });

    return points;
  };

  const points = formatAnswer(answer);

  if (points.length === 1 && points[0].sub.length === 0) {
    return (
      <p className="text-sm text-dark-700 leading-relaxed">{points[0].main}</p>
    );
  }

  return (
    <div className="text-sm text-dark-700 space-y-3">
      {points.map((point, idx) => (
        <div key={idx} className="space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-dark-900 font-medium mt-0.5 shrink-0">•</span>
            <p className="leading-relaxed flex-1">{point.main}</p>
          </div>
          {point.sub.length > 0 && (
            <div className="ml-5 space-y-1.5">
              {point.sub.map((subPoint, subIdx) => (
                <div key={subIdx} className="flex items-start gap-2">
                  <span className="text-dark-600 text-xs mt-1 shrink-0">◦</span>
                  <p className="text-dark-600 leading-relaxed flex-1">
                    {subPoint}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const HelpCenter: React.FC = () => {
  const { t } = useTranslation("help");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(categories.map((c) => c.id)),
  );

  const toggleCategory = (id: string) => {
    const next = new Set(expandedCategories);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedCategories(next);
  };

  const toggleItem = (uid: string) => {
    const next = new Set(expandedItems);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    setExpandedItems(next);
  };

  const catPad = "px-6 py-4";
  const itemPad = "pl-8 pr-4 py-3";
  const qText = "text-sm";

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <HelpCircle className="h-8 w-8 text-primary-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">
            {t("page.title")}
          </h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-gray-600">{t("page.subtitle")}</p>
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {t("page.faqTitle")}
        </h2>

        <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
          {categories.map((cat, cidx) => {
            const isCatOpen = expandedCategories.has(cat.id);
            return (
              <div
                key={cat.id}
                className={cidx !== 0 ? "border-t border-gray-200" : ""}
              >
                <button
                  onClick={() => toggleCategory(cat.id)}
                  className={`w-full ${catPad} text-left flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors`}
                  aria-expanded={isCatOpen}
                  aria-controls={`cat-panel-${cat.id}`}
                >
                  <span className="font-semibold text-gray-900">
                    {t(`categories.${cat.id}.title`)}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-600 transition-transform duration-200 ${
                      isCatOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <Collapsible isOpen={isCatOpen} id={`cat-panel-${cat.id}`}>
                  <div className="border-t border-gray-200">
                    {cat.items.map((item, idx) => {
                      const uid = `${cat.id}:${item.id}`;
                      const isOpen = expandedItems.has(uid);
                      const isFirst = idx === 0;
                      return (
                        <div key={uid}>
                          <button
                            onClick={() => toggleItem(uid)}
                            className={`w-full ${itemPad} text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${
                              !isFirst ? "border-t border-gray-200" : ""
                            }`}
                            aria-expanded={isOpen}
                            aria-controls={`faq-answer-${uid}`}
                          >
                            <span
                              className={`${qText} font-medium text-gray-800`}
                            >
                              {t(
                                `categories.${cat.id}.items.${item.id}.question`,
                              )}
                            </span>
                            <ChevronDown
                              className={`h-4 w-4 text-gray-600 transition-transform duration-200 shrink-0 ml-2 ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          <Collapsible isOpen={isOpen} id={`faq-answer-${uid}`}>
                            <div
                              className={`${itemPad} pl-8 pr-6 pt-2 pb-4 bg-gray-50 border-t border-gray-200`}
                            >
                              <FormattedAnswer
                                answer={t(
                                  `categories.${cat.id}.items.${item.id}.answer`,
                                )}
                              />
                            </div>
                          </Collapsible>
                        </div>
                      );
                    })}
                  </div>
                </Collapsible>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
